import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { Outlet, useLocation, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Theme } from '@astryxdesign/core';
import { Button } from '@astryxdesign/core/Button';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Spinner } from '@astryxdesign/core/Spinner';
import { api } from '@/lib/api';
import { AppError, errorText } from '@/lib/errors';
import { registerStudioWorker } from '@/lib/pwa';
import { brightPalette, type StudioDesign, studioTheme } from '@/lib/theme';
import type { Storefront } from '@/lib/types';
import { NotFoundPage } from '@/app/NotFoundPage';

const StudioCtx = createContext<Storefront | null>(null);
const DesignCtx = createContext<StudioDesign>('classic');

export function useStudio(): Storefront {
  const v = useContext(StudioCtx);
  if (!v) throw new Error('useStudio outside StudioRoot');
  return v;
}

/** The storefront design shown to this visitor; pages with their own layout per design read it. */
export function useDesign(): StudioDesign {
  return useContext(DesignCtx);
}

/** Route element per design: `<ByDesign classic={<HomePage />} bright={<BrightHome />} />`. */
export function ByDesign(props: Record<StudioDesign, ReactNode>) {
  return <>{props[useDesign()]}</>;
}

export function useSlug(): string {
  return useParams().slug!.toLowerCase();
}

export const storefrontKey = (slug: string) => ['storefront', slug] as const;

function onAccent(hex: string) {
  const n = parseInt(hex.slice(1), 16);
  const l = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return l > 0.6 ? '#140F12' : '#FFFFFF';
}

/**
 * The design this visitor sees: the studio's own (`tenants.design`), unless `?design=bright|classic`
 * asked for another one — that preview is remembered for the tab, per studio. Before the storefront
 * loads, the studio's HTML shell says which design it has, so the loader is not the wrong colour;
 * studios without a shell (sales demos) start bright, the default for new studios.
 */
function visitorDesign(slug: string, search: string, studio: StudioDesign | undefined): StudioDesign {
  const key = `beauty.design.${slug}`;
  const asked = new URLSearchParams(search).get('design');
  const shell = document.querySelector('meta[name="tenant"]')?.getAttribute('content') === slug;
  const own = studio ?? (shell && document.querySelector('meta[name="tenant-design"]')?.getAttribute('content') === 'classic' ? 'classic' : 'bright');
  try {
    if (asked === 'bright' || asked === 'classic') sessionStorage.setItem(key, asked);
    const kept = sessionStorage.getItem(key);
    return kept === 'bright' || kept === 'classic' ? kept : own;
  } catch {
    return asked === 'bright' || asked === 'classic' ? asked : own;
  }
}

export function StudioRoot() {
  const slug = useSlug();
  const { pathname, search } = useLocation();
  const q = useQuery({ queryKey: storefrontKey(slug), queryFn: () => api.storefront(slug), staleTime: 60_000 });
  const accent = q.data?.tenant.accent ?? '#E8A598';
  // The owner's cabinet keeps the dark design whatever the clients see.
  const design: StudioDesign = /\/owner(\/|$)/.test(pathname) ? 'classic' : visitorDesign(slug, search, q.data?.tenant.design);
  const theme = studioTheme(accent, design);
  const mode = design === 'bright' ? 'light' : 'dark';

  useEffect(() => {
    void registerStudioWorker(slug);
  }, [slug]);

  // Studios in preview (incl. sales demos made for a salon) stay out of search engines.
  const preview = q.data?.tenant.status === 'preview';
  useEffect(() => {
    if (!preview) return;
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex';
    document.head.append(robots);
    return () => robots.remove();
  }, [preview]);

  // --studio-accent paints buttons and selections. The bright design uses a saturated version of
  // the studio colour and its tints (--br-*), see brightPalette.
  useEffect(() => {
    const root = document.documentElement;
    if (design === 'bright') {
      const p = brightPalette(accent);
      root.style.setProperty('--studio-accent', p.accent);
      root.style.setProperty('--studio-on-accent', '#FFFFFF');
      for (const [k, v] of Object.entries(p)) root.style.setProperty(`--br-${k}`, v);
      root.dataset.design = 'bright';
    } else {
      root.style.setProperty('--studio-accent', accent);
      root.style.setProperty('--studio-on-accent', onAccent(accent));
      delete root.dataset.design;
    }
    // The browser bar and form controls follow the design (index.html starts studio pages bright).
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', design === 'bright' ? '#FFFFFF' : '#0B0A0C');
    document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', design === 'bright' ? 'light' : 'dark');
    if (q.data && !document.querySelector('meta[name="tenant"]')) document.title = `${q.data.tenant.name} — онлайн-запись`;
  }, [accent, design, q.data]);
  useEffect(
    () => () => {
      delete document.documentElement.dataset.design;
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#0B0A0C');
      document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', 'dark');
    },
    [],
  );

  if (q.isPending) {
    return (
      <Theme theme={theme} mode={mode}>
        <main className="app-shell center-screen" aria-busy="true">
          <Spinner size="xl" label="Загружаем студию" />
        </main>
      </Theme>
    );
  }
  if (q.isError) {
    if (q.error instanceof AppError && q.error.code === 'tenant_not_found') {
      return <NotFoundPage title="Студия не найдена" description="Ссылка неверная или студия больше не принимает записи." />;
    }
    return (
      <Theme theme={theme} mode={mode}>
        <main className="app-shell center-screen">
          <EmptyState
            title="Не удалось открыть студию"
            description={errorText(q.error)}
            headingLevel={1}
            actions={<Button label="Повторить" variant="primary" onClick={() => q.refetch()} />}
          />
        </main>
      </Theme>
    );
  }
  return (
    <Theme theme={theme} mode={mode}>
      <StudioCtx.Provider value={q.data}>
        <DesignCtx.Provider value={design}>
          <Outlet />
        </DesignCtx.Provider>
      </StudioCtx.Provider>
    </Theme>
  );
}
